import React, { useEffect, useState } from "react";
import { Alert, Button, Paper, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { getStaffOriginal } from "./staffNotesService";
import { formatCareEvents, formatStaffTime } from "./staffNotePresentation";
export default function StaffOriginalSection({ source, compact = false }) {
  const [state, setState] = useState({
    loading: true,
    original: null,
    error: "",
  });
  useEffect(() => {
    let active = true;
    setState({ loading: true, original: null, error: "" });
    getStaffOriginal(source.id)
      .then((result) => {
        if (active) setState({ ...result, loading: false, error: "" });
      })
      .catch((error) => {
        if (active)
          setState({
            loading: false,
            original: null,
            error: error.message || "The original could not be loaded.",
          });
      });
    return () => {
      active = false;
    };
  }, [
    source.id,
    source.staffOriginalVersion,
    source.staffSignatureId,
    source.staffCorrectionRevision,
  ]);
  if (state.loading)
    return <Typography>Loading the original version…</Typography>;
  if (state.error) return <Alert severity="warning">{state.error}</Alert>;
  const original = state.original,
    version = original?.version,
    signature = original?.signature,
    evidence = state.source || source;
  return (
    <Paper
      variant={compact ? "elevation" : "outlined"}
      elevation={0}
      sx={{ p: compact ? 0 : 2, borderRadius: 3 }}
    >
      <Stack spacing={1}>
        <Typography fontWeight={800}>Caregiver’s original note</Typography>
        <Typography>
          Written by{" "}
          {signature?.signerName ||
            original?.authorName ||
            evidence.authorName ||
            evidence.capturedBy?.name ||
            "Author not recorded"}
        </Typography>
        <Typography>Care: {formatCareEvents(version?.careEvents)}</Typography>
        <Typography>
          Documented:{" "}
          {formatStaffTime(
            version?.documentedAt || evidence.capturedAt || evidence.createdAt,
            state.timeZone || "UTC",
          )}{" "}
          · {state.timeZone || "Timezone not recorded"}
        </Typography>
        <Typography>
          {signature
            ? `Signed · ${signature.signerName} · ${signature.providerTitle} · ${formatStaffTime(signature.signedAt, state.timeZone)}`
            : original?.withdrawn
              ? "Withdrawn · retained in history"
              : "Unsigned"}
        </Typography>
        <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {version?.text ||
            evidence.rawText ||
            evidence.originalText ||
            "The complete original is unavailable."}
        </Typography>
        {(state.addenda || []).map((addendum) => (
          <Stack
            key={addendum.captureId}
            spacing={0.5}
            sx={{ borderLeft: "3px solid", borderColor: "divider", pl: 2 }}
          >
            <Typography fontWeight={700}>
              {addendum.withdrawn
                ? "Withdrawn correction (history)"
                : "Dated correction"}{" "}
              · Documented{" "}
              {formatStaffTime(addendum.version.documentedAt, state.timeZone)}
            </Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {addendum.version.text}
            </Typography>
            <Typography>
              {addendum.signature
                ? `Signed by ${addendum.signature.signerName} · ${addendum.signature.providerTitle} · ${formatStaffTime(addendum.signature.signedAt, state.timeZone || "UTC")}`
                : "Correction unsigned"}
            </Typography>
          </Stack>
        ))}
        {(state.history || []).length ? (
          <Stack spacing={0.5}>
            <Typography fontWeight={700}>Audit history</Typography>
            {state.history.map(item => <Typography key={item.id}>
              {item.operation} · {item.actor} · {formatStaffTime(item.changedAt, state.timeZone || "UTC")}
            </Typography>)}
          </Stack>
        ) : null}
        <Typography color="text.secondary">
          Signatures cover only the original version, not the organized moments
          or fact approvals.
        </Typography>
        <Button
          component={Link}
          to="/my-notes-today"
          sx={{ alignSelf: "flex-start" }}
        >
          Review my original notes
        </Button>
      </Stack>
    </Paper>
  );
}
